import {UIGameMapHighlightParameters, UIGameMapHighlightView} from '../UIGameMapHighlightView'
import {UIGameConfirmActionController} from '../../ui_game_confirmation/UIGameConfirmActionController'
import {GameStateEnums} from '../../../../../../shared/GameEnums'
import {UIGameManager} from '../../UIGameManager'
import {UIGameMechanic} from '../../UIGameEventController'
import {UIUtilsEjsVariables} from '../../../UIUtilsEjsVariables'
import {UIGameBeginnerHintView} from '../../ui_game_beginner_hints/UIGameBeginnerHintView'
import {BeginnerHintFormat, GameAction} from '@colonist/game-base-types'
import {Sound} from '../../../Sound'
import {UIGameSpriteHandler} from '../../../UIGameSpriteHandler'
import {PIXI} from '@colonist/pixi'
import {logError} from '../../../ui_utils/Utils'
import {Sprites} from '../../UIGameImageDictionary'
import {BeginnerModeActionState} from '@colonist/game-state-types'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {TurnState} from '@colonist/game-states'

export abstract class UIGameMapHighlightController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager

    readonly confirmActionController: UIGameConfirmActionController
    protected highlightViews: UIGameMapHighlightView[]
    protected readonly buttonScaleForMobile: number
    protected readonly circlesScaleForMobile: number
    protected beginnerHintView?: UIGameBeginnerHintView | undefined
    protected beginnerHint: BeginnerHintFormat

    protected allowedPlacementIndexes?: number[] | undefined
    protected currentBeginnerModeAction?: BeginnerModeActionState

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.highlightViews = []
        this.buttonScaleForMobile = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI ? 1.25 : (UIUtilsEjsVariables.isUserOnMobile ? 1.75 : 1)
        this.circlesScaleForMobile = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI ? 1.4 : (UIUtilsEjsVariables.isUserOnMobile ? 1.5 : 1)

        this.confirmActionController = new UIGameConfirmActionController(this.uiGameManager)
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.GameEnd: this.removeHighlights(); break
            case UIGameEventEnums.BeginnerHintReceived: this.beginnerHintReceived(data); break
            case UIGameEventEnums.GameStateUpdated: this.destroyBeginnerHintView(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            default: break
        }
    }

    protected abstract updateHighlights(): void

    protected removeHighlights() {
        for(const view of this.highlightViews) {
            this.uiGameManager.mapController.mapView.removeChild(view)
        }
        this.highlightViews = []
        this.confirmActionController.cleanupConfirmation()
    }

    protected highlight(highlightParameters: UIGameMapHighlightParameters, action: () => void) {
        const highlightView = new UIGameMapHighlightView(this.uiGameManager, highlightParameters, this.uiGameManager.mapController.mapView, action)
        this.highlightViews.push(highlightView)
    }

    protected startAnimation() {
        this.animateCircles()
    }

    protected endAnimation() {
        for(const highlightView of this.highlightViews) highlightView.resetAnimation()
    }

    protected destroyBeginnerHintView() {
        this.beginnerHintView?.destroy()
        this.beginnerHintView = undefined
    }

    protected hideBeginnerHintInformation() {
        this.beginnerHintView?.hideInformation()
    }

    protected isAllowedPlacementIndex(index: number): boolean {
        if(this.allowedPlacementIndexes == undefined) return true
        if(this.uiGameManager.cardController.beginnerModeHelper?.areCardsBeingDealt) return false
        if(this.currentBeginnerModeAction?.type == GameAction.SkippedWelcome) this.uiGameManager.socketGameSend.skipWelcomeActionOnBeginnerMode()
        return this.allowedPlacementIndexes.includes(index)
    }

    private animateCircles() {
        if(this.highlightViews.length <= 0) this.endAnimation()
        for(const highlightView of this.highlightViews) {
            highlightView.animate()
        }
    }

    protected beginnerHintReceived(data: BeginnerHintFormat) {
        const possibleMapActionSuggestions = [GameAction.ConfirmBuildSettlement, GameAction.ConfirmBuildCity, GameAction.ConfirmBuildRoad]
        if(!possibleMapActionSuggestions.includes(data.type)) return

        const targetHighlightIndex = data.data
        for(const highlight of this.highlightViews) {
            if(highlight.parent == undefined) continue
            if(highlight.highlightedGameElementIndex != targetHighlightIndex) continue
            if(this.confirmActionController.selectedLocationIndex == targetHighlightIndex) return

            this.destroyBeginnerHintView()
            Sound.playSound(Sound.SoundType.BeginnerHintActivated)
            const descriptionContext = this.getBeginnerTipDescriptionContext(data.type)
            const tipTexture = this.getBeginnerTipTexture(data.type)

            this.beginnerHintView = new UIGameBeginnerHintView(this.uiGameManager, highlight, {key: `strings:game.beginnerTips.actionHints.description`, options: {context: descriptionContext}}, tipTexture)
            this.beginnerHint = data
            highlight.resetDisplay()
        }
    }

    protected onCanvasResize() {
        if(this.beginnerHintView == undefined) return
        this.beginnerHintReceived(this.beginnerHint)
    }

    protected onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        this.currentBeginnerModeAction = data
        this.allowedPlacementIndexes = data.allowedPlacementIndexes
    }

    private getBeginnerTipDescriptionContext(suggestedAction: GameAction): string {
        const isInitialPlacements = this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement

        switch(suggestedAction) {
            case GameAction.ConfirmBuildSettlement: return isInitialPlacements ? 'initialPlacementCorner' : 'buildSettlement'
            case GameAction.ConfirmBuildCity: return isInitialPlacements ? 'initialPlacementCorner' : 'buildCity'
            case GameAction.ConfirmBuildRoad: return 'buildRoad'
            default:
                logError('getDescriptionContext', ['Invalid suggested action', suggestedAction])
                return ''
        }
    }

    private getBeginnerTipTexture(suggestedAction: GameAction): PIXI.Texture {
        const isInitialPlacements = this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement
        const initialPlacementCornerTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_beginner_hint_initial_placement)

        switch(suggestedAction) {
            case GameAction.ConfirmBuildSettlement: return isInitialPlacements ? initialPlacementCornerTexture : UIGameSpriteHandler.getSettlementForPlayer(this.uiGameManager.gameController.myColor)
            case GameAction.ConfirmBuildCity: return UIGameSpriteHandler.getCityForPlayer(this.uiGameManager.gameController.myColor)
            case GameAction.ConfirmBuildRoad: return UIGameSpriteHandler.getRoadForPlayer(this.uiGameManager.gameController.myColor)
            default:
                logError('getBeginnerTipTexture', ['Invalid suggested action', suggestedAction])
                return PIXI.Texture.EMPTY
        }
    }
}
